import { connect } from 'react-redux';
import {
  useContext, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import EpisodePreviewSmallList from '@/components/app/EpisodePreviews/EpisodePreviewSmallList';
import { isEqualAsNumber } from '@/helpers/math';
import { scrollTo } from '@/helpers/app-layout';
import calculateEpisodeMenuStyle from '@/helpers/episode-menu/episode-preview-desktop';
import RightClickMenuContext from '@/contexts/right-click-menu';
import { selectCurrentEpisodeId, selectKeakiePlaybackMode } from '@/state/selectors/audio';
import { HEADER_HEIGHT_DESKTOP, HEADER_HEIGHT_MOBILE } from '@/config/layout';
import useShouldRenderForMobile from '@/hooks/layout/useShouldRenderForMobile';

const ChannelEpisodes = ({
  currentEpisodeId, // The id of the currently playing episode
  keakiePlaybackMode,
  loading,
  loadingCount,
  episodePreviews,
  scrollToEpisodeNumber, // The number of the episode we should scroll to
  scrollToSeasonNumber, // The season number of the episode we should scroll to
  scrollingRef, // A reference to the container we need to scroll to get to the episode
  shouldScrollToEpisode, // Whether we need to scroll to the episode
  onScrolledToEpisode, // Function to call when we complete the scroll to episode
  haveSetSeasonFromUrl, // True if we have already set the season from the route in state,
  onClickPlayFromChannel,
  seasonId,
  isPaused,
  extraPadding
}) => {
  // Calculate the screen size
  const shouldRenderForMobile = useShouldRenderForMobile();

  // Retrieve the right click toggle function
  const { toggleRightClickMenu } = useContext(RightClickMenuContext);

  // Handle toggling the right click menu
  const handleClickMenu = (e, episode, elemId) => {
    toggleRightClickMenu({
      e,
      episode,
      elemId,
      props: {
        forceQueueAdd: true,
        calculateEpisodeMenuStyle,
        excludeMenuOptionKeys: [
          'goToChannel'
        ]
      }
    });
  };

  // Handle the clicking of an episode
  const handleClickEpisode = (id, episode) => {
    onClickPlayFromChannel({
      episode,
      wasClickedDirectly: isEqualAsNumber(scrollToEpisodeNumber, episode.episode_number)
        && isEqualAsNumber(scrollToSeasonNumber, episode.season)
    });
  };

  const highlightTimerRef = useRef(null);
  const episodeNodeRef = useRef(null);

  // This logic should be brought out of the component!!!
  const highlightEpisodePreviewNode = ({ episodeNode }) => {
    episodeNodeRef.current = episodeNode;

    highlightTimerRef.current = setTimeout(() => {
      episodeNode.classList.add('--is-highlighted');
  
      setTimeout(() => {
        episodeNode.classList.remove('--is-highlighted');
      }, 5000);
    }, 500);
  };

  const cancelHighlight = () => {
    clearTimeout(highlightTimerRef.current);

    if (episodeNodeRef.current) {
      episodeNodeRef.current.classList.remove('--is-highlighted');
    }
  };

  // Handle scrolling to the episode if necessary
  useEffect(() => {
    // Timeout to ensure the scroll-to-episode id has been set
    setTimeout(() => {
      // Get the desktop channel section element so that we can scroll down to it if in desktop mode
      const desktopChannelSection = document.getElementById('desktop-channel-top-section');
      // Find the scroll to episode element in the dom
      const episodeToScrollTo = document.getElementById('scroll-to-episode');
      // Reference the DOM element which holds the episode we need to scroll to
      const scrollContainer = (scrollingRef || {}).current;

      // Only scroll to the episode IF:
      if (
        !!shouldScrollToEpisode // We need scroll to the episode
          && !loading // The episodes are not loading
          && !!haveSetSeasonFromUrl // We have already set the season from the route into state
          && !!episodeToScrollTo // We have an episode preview element to scroll to
          && !!(shouldRenderForMobile ? true : desktopChannelSection) // We have the channel section container
          && !!scrollContainer // We have the episodes list container
      ) {
        setTimeout(() => {
          // If in desktop mode, we need to scroll the page down to the channel content section
          if (!shouldRenderForMobile) {
            scrollTo({
              top: desktopChannelSection.getBoundingClientRect().top - HEADER_HEIGHT_DESKTOP,
              behavior: 'smooth'
            });
          }
    
          // Calculate the position to scroll to within the episodes container
          const scrollTopPosition = shouldRenderForMobile
            ? episodeToScrollTo.getBoundingClientRect().top - HEADER_HEIGHT_MOBILE - 200
            : episodeToScrollTo.offsetTop - scrollContainer.offsetTop;

          // Perform the scroll
          if (shouldRenderForMobile) {
            scrollTo({
              top: scrollTopPosition,
              behavior: 'smooth'
            });
          } else {
            scrollContainer.scrollTo({
              top: ((scrollContainer.scrollHeight - scrollTopPosition) < 200)
                ? scrollContainer.scrollHeight
                : (scrollTopPosition - 100),
              behavior: 'smooth'
            });
          }

          // Call the scrolled to episode callback
          if (onScrolledToEpisode) {
            onScrolledToEpisode();
          }
    
          // Handle highlighting and unhighlighting of the episode preview
          highlightEpisodePreviewNode({ episodeNode: episodeToScrollTo });
        }, 250);
      }
    }, 0);
  }, [
    scrollingRef,
    scrollToEpisodeNumber,
    scrollToSeasonNumber,
    loading,
    haveSetSeasonFromUrl,
    shouldRenderForMobile,
    shouldScrollToEpisode,
    onScrolledToEpisode
  ]);

  // Handle cancelling of the highlight
  useEffect(() => {
    if (haveSetSeasonFromUrl && !isEqualAsNumber(seasonId, scrollToSeasonNumber)) {
      cancelHighlight();
    }
  }, [scrollToSeasonNumber, seasonId, haveSetSeasonFromUrl]);

  const isChannelMode = (keakiePlaybackMode === 'show');

  return (
    <div className='channel-episodes-wrapper'>
      <EpisodePreviewSmallList
        currentlyPlayingEpisodeId={ isChannelMode ? currentEpisodeId : null }
        episodes={ episodePreviews || [] }
        getEpisodeProps={ (episode) => ({
          onClick: handleClickEpisode,
          onClickMenu: handleClickMenu,
          loading,
          elementIdString: 'current-channel-episode',
          type: 'image',
          showDescription: true,
          showDuration: true,
          alignContentTop: true,
          showPlayPauseIcon: isChannelMode && (episode.id === currentEpisodeId),
          isPaused: isChannelMode && (episode.id === currentEpisodeId) && isPaused,
          extraPadding
        }) }
        loading={ loading }
        loadingCount={ loadingCount }
        scrollToEpisodeNumber={ scrollToEpisodeNumber }
        scrollToSeasonNumber={ scrollToSeasonNumber }
      />
    </div>
  );
};

ChannelEpisodes.propTypes = {
  currentEpisodeId: PropTypes.string,
  keakiePlaybackMode: PropTypes.string,
  loading: PropTypes.bool,
  episodePreviews: PropTypes.array,
  scrollToEpisodeNumber: PropTypes.number,
  scrollToSeasonNumber: PropTypes.number,
  scrollingRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object
  ]),
  shouldScrollToEpisode: PropTypes.bool,
  onScrolledToEpisode: PropTypes.func,
  haveSetSeasonFromUrl: PropTypes.bool,
  onClickPlayFromChannel: PropTypes.func.isRequired,
  seasonId: PropTypes.string,
  isPaused: PropTypes.bool,
  extraPadding: PropTypes.bool,
  loadingCount: PropTypes.number
};

ChannelEpisodes.defaultProps = {
  currentEpisodeId: null,
  keakiePlaybackMode: null,
  loading: false,
  episodePreviews: [],
  scrollToEpisodeNumber: null,
  scrollToSeasonNumber: null,
  scrollingRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object
  ]),
  shouldScrollToEpisode: false,
  onScrolledToEpisode: undefined,
  haveSetSeasonFromUrl: false,
  isPaused: false,
  extraPadding: false,
  seasonId: '',
  loadingCount: 4
};

// ------ Connect the page with the store and inject required state as props ------
const mapStateToProps = (state) => ({
  currentEpisodeId: selectCurrentEpisodeId(state),
  keakiePlaybackMode: selectKeakiePlaybackMode(state)
});

export default connect(mapStateToProps)(ChannelEpisodes);